import { Box, Card, CardContent, Chip, Stack, Typography } from "@mui/material";
import {
  BuildingOfficeIcon,
  Cube,
  CurrencyDollarIcon,
  PackageIcon
} from "@phosphor-icons/react";
import { useAssetsPaginated } from "features/assets";
import { FILTER_OPERATIONS } from "features/members";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import { useChurchStore } from "../store";

export const Assets = () => {
  const [activeTab, setActiveTab] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [allAssets, setAllAssets] = useState([]);
  const [hasMore, setHasMore] = useState(true);

  const { t } = useTranslation();

  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const assetsPaginatedQuery = useAssetsPaginated({
    churchId: selectedChurch?.id,
    page: currentPage,
    pageSize: 10,
    queries:
      activeTab === "All"
        ? {}
        : {
            filter: {
              type: [
                {
                  operator: FILTER_OPERATIONS.EQUAL,
                  value: activeTab
                }
              ]
            }
          }
  });

  const tabs = [
    {
      name: t("churches.profile.allAsset"),
      count: 2,
      active: true,
      value: "All",
      showCount: false
    },
    {
      name: t("churches.profile.buildings"),
      count: 2,
      value: "Building",
      showCount: false
    },
    {
      name: t("churches.profile.land"),
      count: 0,
      value: "Land",
      showCount: false
    },
    {
      name: t("churches.profile.vehicles"),
      count: 0,
      value: "Vehicle",
      showCount: false
    },
    {
      name: t("churches.profile.equipment"),
      count: 0,
      value: "Equipment",
      showCount: false
    },
    {
      name: t("churches.profile.business"),
      count: 0,
      value: "Business",
      showCount: false
    },
    {
      name: t("churches.profile.other"),
      count: 0,
      value: "Other",
      showCount: false
    }
  ];

  // Helper function to get condition colors
  const getConditionColors = condition => {
    switch (condition?.toUpperCase()) {
      case "NEW":
      case "EXCELLENT":
        return { color: "#10B981", bg: "#D1FAE5" };
      case "GOOD":
        return { color: "#3B82F6", bg: "#DBEAFE" };
      case "FAIR":
        return { color: "#F59E0B", bg: "#FEF3C7" };
      case "POOR":
        return { color: "#EF4444", bg: "#FEE2E2" };
      default:
        return { color: "#6B7280", bg: "#F3F4F6" };
    }
  };

  // Helper function to format date
  const formatDate = dateString => {
    if (!dateString) return "N/A";
    return moment(dateString).format("DD/MM/YYYY"); // dd/mm/yyyy format
  };

  // Helper function to format currency
  const formatCurrency = amount => {
    if (!amount) return "0";
    return amount.toLocaleString();
  };

  // Reset pagination when tab changes
  useEffect(() => {
    setCurrentPage(1);
    setAllAssets([]);
    setHasMore(true);
  }, [activeTab, selectedChurch?.id]);

  // Handle new data from query
  useEffect(() => {
    if (assetsPaginatedQuery.data?.result?.data) {
      const newAssets = assetsPaginatedQuery.data.result.data;
      const pagination = assetsPaginatedQuery.data.result;

      if (currentPage === 1) {
        // First page - replace all assets
        setAllAssets(newAssets);
      } else {
        // Additional pages - append to existing assets
        setAllAssets(prev => [...prev, ...newAssets]);
      }

      // Check if there are more pages
      setHasMore(pagination.current_page < pagination.last_page);
    }
  }, [assetsPaginatedQuery.data, currentPage]);

  // Function to fetch more data for infinite scroll
  const fetchMoreData = useCallback(() => {
    if (!assetsPaginatedQuery.isLoading && hasMore) {
      setCurrentPage(prev => prev + 1);
    }
  }, [assetsPaginatedQuery.isLoading, hasMore]);

  // Get assets for display
  const assets = allAssets;

  return (
    <Stack>
      {/* Stat Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
        {/* Total Asset Value Card */}
        <Card
          sx={{
            flex: 1,
            backgroundColor: "#F8ECE3",
            border: "none",
            borderRadius: "16px",
            boxShadow: "none"
          }}
          elevation={0}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <CurrencyDollarIcon size={24} color="#FE964A" />
              </div>
              <Stack>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#FE964A",
                    fontSize: "2rem",
                    mb: 0.5
                  }}
                >
                  {formatCurrency(
                    assetsPaginatedQuery.data?.result?.total_asset_value
                  )}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#FE964A",
                    fontWeight: 500,
                    fontSize: "0.875rem"
                  }}
                >
                  {t("churches.profile.totalAssetValue")}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Total Assets Count Card */}
        <Card
          sx={{
            flex: 1,
            backgroundColor: "#E5EDF9",
            border: "none",
            borderRadius: "16px",
            boxShadow: "none"
          }}
          elevation={0}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <PackageIcon size={24} color="#2F78EE" />
              </div>
              <Stack>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#2F78EE",
                    fontSize: "2rem",
                    mb: 0.5
                  }}
                >
                  {assetsPaginatedQuery.data?.result?.total_assets || 0}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#2F78EE",
                    fontWeight: 500,
                    fontSize: "0.875rem"
                  }}
                >
                  {t("churches.profile.totalAssets")}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Card
        sx={{
          borderRadius: "20px",
          border: "1px solid #e5e7eb",
          p: 3
        }}
        elevation={0}
      >
        <Stack direction="row" alignItems="center" spacing={3} mb={3}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Cube size={24} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, textWrap: "nowrap" }}
            >
              {t("churches.profile.fixedAsset")}
            </Typography>
          </Stack>

          {/* Tabs */}
          <Stack direction="row" spacing={1} sx={{ gap: 1 }}>
            {tabs.map(tab => (
              <Chip
                key={tab.value}
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <span>{tab.name}</span>
                    {tab.showCount && (
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          backgroundColor:
                            activeTab === tab.value ? "#1f8990" : "#e7e7e7",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color:
                              activeTab === tab.value ? "#fff" : "text.primary"
                          }}
                        >
                          {tab.count}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                }
                onClick={() => setActiveTab(tab.value)}
                clickable
                variant={activeTab === tab.value ? "filled" : "outlined"}
                color={activeTab === tab.value ? "primary" : "default"}
              />
            ))}
          </Stack>
        </Stack>

        {/* Assets Grid with Infinite Scroll */}
        {assetsPaginatedQuery.isLoading && currentPage === 1 ? (
          <Typography>{t("churches.profile.loadingAssets")}</Typography>
        ) : assetsPaginatedQuery.isError ? (
          <Typography color="error">
            {t("churches.profile.errorLoadingAssets")}
          </Typography>
        ) : assets.length === 0 && !assetsPaginatedQuery.isLoading ? (
          <Typography>{t("churches.profile.noAssetsFound")}</Typography>
        ) : (
          <InfiniteScroll
            dataLength={assets.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={
              <Stack alignItems="center" sx={{ py: 2 }}>
                <Typography>
                  {t("churches.profile.loadingMoreAssets")}
                </Typography>
              </Stack>
            }
            endMessage={null}
          >
            <Stack spacing={3}>
              {assets.map(asset => {
                const conditionColors = getConditionColors(asset.condition);
                return (
                  <Card
                    key={asset.id}
                    sx={{
                      borderRadius: "16px",
                      border: "1px solid #e5e7eb"
                    }}
                    elevation={0}
                  >
                    <CardContent sx={{ p: 3 }}>
                      {/* Asset Header */}
                      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                        <Box
                          style={{
                            width: "48px",
                            height: "48px",
                            backgroundColor: "#e7e7e7",
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 1
                          }}
                        >
                          <BuildingOfficeIcon size={32} />
                        </Box>
                        <Stack spacing={1} sx={{ flex: 1 }}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="start"
                          >
                            <Stack>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "12px" }}
                              >
                                {t("churches.profile.assetName")}
                              </Typography>
                              <Typography sx={{ fontWeight: 600 }}>
                                {asset.name}
                              </Typography>
                            </Stack>
                            <Stack alignItems="end">
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "12px" }}
                              >
                                {t("churches.profile.assetType")}
                              </Typography>
                              <Chip
                                label={asset.type}
                                size="small"
                                variant="outlined"
                              />
                            </Stack>
                          </Stack>
                        </Stack>
                      </Stack>

                      {/* Description */}
                      <Stack spacing={1} sx={{ mb: 3 }}>
                        <Typography variant="body2">
                          {t("churches.profile.description")}
                        </Typography>
                        <Typography variant="body2">
                          {asset.description || "No description provided"}
                        </Typography>
                      </Stack>

                      {/* Location */}
                      <Stack spacing={1} sx={{ mb: 3 }}>
                        <Typography variant="body2">
                          {t("churches.profile.location")}
                        </Typography>
                        <Typography variant="body2">
                          {asset.location || "No location specified"}
                        </Typography>
                      </Stack>

                      {/* Financial Info Row */}
                      <Stack direction="row" spacing={4} sx={{ mb: 3 }}>
                        <Stack spacing={1} sx={{ flex: 1 }}>
                          <Typography variant="body2">
                            {t("churches.profile.acquisitionDate")}
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(asset.acquisition_date)}
                          </Typography>
                        </Stack>
                        <Stack spacing={1} sx={{ flex: 1 }}>
                          <Typography variant="body2">
                            {t("churches.profile.purchasePrice")}
                          </Typography>
                          <Typography variant="body2">
                            {formatCurrency(asset.purchase_price)}
                          </Typography>
                        </Stack>
                        <Stack spacing={1} sx={{ flex: 1 }}>
                          <Typography variant="body2">
                            {t("churches.profile.bookValue")}
                          </Typography>
                          <Typography variant="body2">
                            {formatCurrency(asset.book_value)}
                          </Typography>
                        </Stack>
                      </Stack>

                      {/* Additional Info Row */}
                      <Stack direction="row" spacing={4} sx={{ mb: 3 }}>
                        <Stack spacing={1} sx={{ flex: 1 }}>
                          <Typography variant="body2">
                            {t("churches.profile.quantity")}
                          </Typography>
                          <Typography variant="body2">
                            {asset.quantity || 0}
                          </Typography>
                        </Stack>
                        <Stack spacing={1} sx={{ flex: 1 }}>
                          <Typography variant="body2">
                            {t("churches.profile.acquisitionMethod")}
                          </Typography>
                          <Typography variant="body2">
                            {asset.acquisition_method || "N/A"}
                          </Typography>
                        </Stack>
                        <Stack spacing={1} sx={{ flex: 1 }}>
                          <Typography variant="body2">
                            {t("churches.profile.status")}
                          </Typography>
                          <Chip
                            label={asset.status}
                            size="small"
                            variant="filled"
                            color={
                              asset.status === "ACTIVE" ? "success" : "default"
                            }
                            sx={{
                              alignSelf: "flex-start"
                            }}
                          />
                        </Stack>
                      </Stack>

                      {/* Maintenance Info Row */}
                      <Stack direction="row" spacing={4}>
                        <Stack spacing={1} sx={{ flex: 1 }}>
                          <Typography variant="body2">
                            {t("churches.profile.condition")}
                          </Typography>
                          <Chip
                            label={asset.condition}
                            size="small"
                            variant="filled"
                            sx={{
                              backgroundColor: conditionColors.bg,
                              color: conditionColors.color,
                              width: "fit-content"
                            }}
                          />
                        </Stack>
                        <Stack spacing={1} sx={{ flex: 1 }}>
                          <Typography variant="body2">
                            {t("churches.profile.lastMaintenance")}
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(asset.last_maintenance_date)}
                          </Typography>
                        </Stack>
                        <Stack spacing={1} sx={{ flex: 1 }}>
                          <Typography variant="body2">
                            {t("churches.profile.nextMaintenance")}
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(asset.maintenance_schedule)}
                          </Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          </InfiniteScroll>
        )}
      </Card>
    </Stack>
  );
};
